/**
 * Create Company File — Cloud GM Company Agent (Feb 2026 / iter-181).
 *
 * Improvements over iter-177:
 *  • Consistent design via <AgentPageShell> (matches ControlCloudGM pattern).
 *  • Phone + email fields already covered by the AI license extractor.
 *  • New "Company Agent" section: pick a REGISTERED agent from
 *    /api/admin/assigned-tasks/targets/search?type=agent OR enter a new one
 *    (name + email + phone).
 *  • On save → navigate back to the 5-button hub (not the detail page).
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Upload, Sparkles, Loader2, Save, Trash2, Plus, FileText, UserPlus, Search, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';
import { isValidEmail, isValidPhone } from '../utils/validators';
import { AgentPageShell } from './agent/AgentPageShell';
import { LinkedAgentTaskPicker, taskToSnapshot } from './agent/LinkedAgentTaskPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyCompany = {
  company_name_ar: '', company_name_en: '',
  license_number: '', unified_license_number: '',
  issuing_authority: '', license_type: '', legal_form: '',
  issue_date: '', expiry_date: '', capital: '',
  activities: [], partners: [],
  address: '', po_box: '', phone: '', email: '',
  notes: '',
  // Feb 2026 — iter-181 company agent
  agent_id: null, agent_name: '', agent_email: '', agent_phone: '',
  // Feb 2026 — iter-200 linked agent task (replaces linked tax invoice)
  linked_agent_task_id: null,
  linked_agent_task_ref: '',
  linked_agent_task_snapshot: null,
};

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const [prefix, base64] = String(reader.result).split(',');
      const m = /^data:([^;]+);base64$/.exec(prefix);
      resolve({ base64, mime: m ? m[1] : file.type });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const AgentCompanyCreatePage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [form, setForm] = useState(emptyCompany);
  const [licenseFile, setLicenseFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Agent picker — mirrors AssignedTaskCreatePage flow
  const [agentMode, setAgentMode] = useState('none');   // 'none' | 'registered' | 'new'
  const [agentQ, setAgentQ] = useState('');
  const [agentResults, setAgentResults] = useState([]);
  const [agentSearching, setAgentSearching] = useState(false);
  const searchTimer = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const breadcrumb = [
    { label_ar: 'الرئيسية', label_en: 'Home', path: '/dashboard' },
    { label_ar: 'Cloud GM Company Agent', label_en: 'Cloud GM Company Agent', path: '/manage/cloudgm-agent' },
    { label_ar: 'إنشاء ملف شركة', label_en: 'Create Company File', path: null },
  ];

  // Debounced agent search
  useEffect(() => {
    if (agentMode !== 'registered' || !agentQ.trim() || agentQ.trim().length < 2) {
      setAgentResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setAgentSearching(true);
      try {
        const r = await axios.get(`${API}/admin/assigned-tasks/targets/search`, {
          headers: getAuthHeaders(),
          params: { type: 'agent', q: agentQ.trim() },
        });
        setAgentResults(r.data?.data || []);
      } catch { setAgentResults([]); }
      finally { setAgentSearching(false); }
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [agentQ, agentMode]);

  const pickAgent = useCallback((u) => {
    setForm((f) => ({
      ...f,
      agent_id: u.id,
      agent_name: u.name || '',
      agent_email: u.email || '',
      agent_phone: u.phone || '',
    }));
    setAgentQ(u.name || u.email || '');
    setAgentResults([]);
  }, []);

  const clearAgent = () => {
    setForm((f) => ({
      ...f,
      agent_id: null, agent_name: '', agent_email: '', agent_phone: '',
      // Feb 2026 iter-200 — task link must follow the agent's lifecycle.
      linked_agent_task_id: null,
      linked_agent_task_ref: '',
      linked_agent_task_snapshot: null,
    }));
    setAgentQ(''); setAgentResults([]);
  };

  const handleLinkedTask = useCallback((task) => {
    if (!task) {
      setForm((f) => ({
        ...f,
        linked_agent_task_id: null,
        linked_agent_task_ref: '',
        linked_agent_task_snapshot: null,
      }));
      return;
    }
    setForm((f) => ({
      ...f,
      linked_agent_task_id: task.id,
      linked_agent_task_ref: task.request_no_display || '',
      linked_agent_task_snapshot: taskToSnapshot(task),
    }));
  }, []);

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(isAr ? 'الحجم الأقصى 10 ميغا' : 'Max 10 MB');
      return;
    }
    const { base64, mime } = await readFileAsBase64(file);
    setLicenseFile({ base64, mime, name: file.name });
  };

  const runAIExtract = async () => {
    if (!licenseFile) {
      toast.error(isAr ? 'ارفع الرخصة أولاً' : 'Upload the license first');
      return;
    }
    setExtracting(true);
    try {
      const res = await axios.post(
        `${API}/agent/companies/extract-license`,
        { file_base64: licenseFile.base64, mime: licenseFile.mime },
        { headers: getAuthHeaders(), timeout: 90_000 }
      );
      const data = res.data?.data || {};
      setForm((f) => ({
        ...f,
        company_name_ar:  data.company_name_ar  || f.company_name_ar,
        company_name_en:  data.company_name_en  || f.company_name_en,
        license_number:   data.license_number   || f.license_number,
        unified_license_number: data.unified_license_number || f.unified_license_number,
        issuing_authority: data.issuing_authority || f.issuing_authority,
        license_type:     data.license_type     || f.license_type,
        legal_form:       data.legal_form       || f.legal_form,
        issue_date:       data.issue_date       || f.issue_date,
        expiry_date:      data.expiry_date      || f.expiry_date,
        capital:          data.capital          || f.capital,
        activities:       Array.isArray(data.activities) && data.activities.length ? data.activities : f.activities,
        partners:         Array.isArray(data.partners)   && data.partners.length   ? data.partners   : f.partners,
        address:          data.address          || f.address,
        po_box:           data.po_box           || f.po_box,
        phone:            data.phone            || f.phone,
        email:            data.email            || f.email,
      }));
      toast.success(isAr ? 'تمت قراءة الرخصة بنجاح' : 'License read successfully');
    } catch (err) {
      // iter-244 — Backend returns 400 with `detail={code,message,document_type}`
      // when the AI decides the file isn't a UAE trade license.
      const detail = err.response?.data?.detail;
      let msg = err.message;
      if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
        if (detail.document_type) msg = `${msg} — ${isAr ? 'النوع المكتشف' : 'Detected type'}: ${detail.document_type}`;
      } else if (typeof detail === 'string') {
        msg = detail;
      }
      toast.error((isAr ? 'تعذّرت القراءة: ' : 'AI extract failed: ') + msg);
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!form.company_name_ar && !form.company_name_en) {
      toast.error(isAr ? 'اسم الشركة (عربي أو إنجليزي) إلزامي' : 'Company name (AR or EN) is required');
      return;
    }
    // Agent validation — only when a mode is chosen
    if (agentMode === 'registered' && !form.agent_id) {
      toast.error(isAr ? 'اختر وكيلاً مسجّلاً من قائمة البحث' : 'Pick a registered agent from the search list');
      return;
    }
    if (agentMode === 'new' && !form.agent_name.trim() && !form.agent_phone.trim() && !form.agent_email.trim()) {
      toast.error(isAr ? 'أدخل اسم الوكيل أو هاتفه أو بريده' : 'Enter agent name, phone or email');
      return;
    }
    // Feb 2026 (iter-184) — validate optional-but-formatted fields.
    if (form.phone && !isValidPhone(form.phone)) {
      toast.error(isAr ? 'رقم هاتف الشركة غير صحيح' : 'Company phone number is not valid');
      return;
    }
    if (form.email && !isValidEmail(form.email)) {
      toast.error(isAr ? 'البريد الإلكتروني للشركة غير صحيح' : 'Company email is not valid');
      return;
    }
    if (form.agent_phone && !isValidPhone(form.agent_phone)) {
      toast.error(isAr ? 'رقم هاتف الوكيل غير صحيح' : 'Agent phone number is not valid');
      return;
    }
    if (form.agent_email && !isValidEmail(form.agent_email)) {
      toast.error(isAr ? 'بريد الوكيل الإلكتروني غير صحيح' : 'Agent email is not valid');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        license_file: licenseFile?.base64 || null,
        license_file_mime: licenseFile?.mime || '',
        license_file_name: licenseFile?.name || '',
      };
      await axios.post(`${API}/agent/companies`, payload, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم حفظ ملف الشركة' : 'Company file saved');
      // Feb 2026 — iter-181: return to the 5-button hub, not the detail page.
      navigate('/manage/cloudgm-agent');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  // partner + activity helpers
  const addPartner = () => setForm((f) => ({ ...f, partners: [...f.partners, { name: '', share: '' }] }));
  const setPartner = (idx, key, val) => setForm((f) => ({
    ...f, partners: f.partners.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
  }));
  const removePartner = (idx) => setForm((f) => ({ ...f, partners: f.partners.filter((_, i) => i !== idx) }));

  const addActivity = () => setForm((f) => ({ ...f, activities: [...f.activities, ''] }));
  const setActivity = (idx, val) => setForm((f) => ({ ...f, activities: f.activities.map((a, i) => (i === idx ? val : a)) }));
  const removeActivity = (idx) => setForm((f) => ({ ...f, activities: f.activities.filter((_, i) => i !== idx) }));

  return (
    <AgentPageShell
      breadcrumb={breadcrumb}
      maxWidth="max-w-5xl"
      onBack={() => navigate('/manage/cloudgm-agent')}
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white" data-testid="agent-company-create-title">
          {isAr ? 'إنشاء ملف شركة' : 'Create Company File'}
        </h1>
      </div>

      {/* 1. License upload + AI extract */}
      <motion.section
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-emerald-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'قراءة الرخصة تلقائياً (AI)' : 'Auto-read license (AI)'}
          </h2>
        </div>
        <p className="text-sm text-[#808080] mb-4">
          {isAr
            ? 'ارفع صورة أو PDF للرخصة التجارية، وسيتم تعبئة الحقول (بما فيها الهاتف والبريد الإلكتروني) تلقائياً.'
            : 'Upload the trade license (image or PDF); AI will auto-fill the fields (including phone & email).'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <label className="flex items-center gap-2 px-4 py-2 bg-[#1a1a2e] border border-[#2a2a4e] rounded-xl cursor-pointer hover:bg-[#2a2a4e]">
            <Upload className="h-4 w-4 text-white" />
            <span className="text-sm text-white">
              {licenseFile ? licenseFile.name : (isAr ? 'اختر ملف الرخصة' : 'Choose license file')}
            </span>
            <input type="file" accept="image/*,application/pdf" className="hidden"
                   onChange={handleFilePick} data-testid="agent-license-file-input" />
          </label>
          <Button
            onClick={runAIExtract}
            disabled={!licenseFile || extracting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
            data-testid="agent-extract-license-btn"
          >
            {extracting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Sparkles className="h-4 w-4 me-2" />}
            {isAr ? 'قراءة بالذكاء الاصطناعي' : 'Read with AI'}
          </Button>
        </div>
      </motion.section>

      {/* 2. Company Agent (iter-181) */}
      <motion.section
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
      >
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-5 w-5 text-cyan-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'وكيل الشركة (اختياري)' : 'Company Agent (optional)'}
          </h2>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {['none', 'registered', 'new'].map((mode) => (
            <button
              key={mode}
              onClick={() => { setAgentMode(mode); if (mode === 'none') clearAgent(); }}
              data-testid={`agent-mode-${mode}`}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                agentMode === mode
                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                  : 'bg-transparent border-white/10 text-[#808080] hover:bg-white/5'
              }`}
            >
              {mode === 'none'       ? (isAr ? 'بدون وكيل'           : 'No agent')
                : mode === 'registered' ? (isAr ? 'وكيل مسجّل'          : 'Registered agent')
                : (isAr ? 'وكيل جديد (غير مسجّل)' : 'New agent (unregistered)')}
            </button>
          ))}
        </div>

        {agentMode === 'registered' && (
          <div className="relative">
            <div className="flex items-center gap-2 bg-[#050505] border border-white/10 rounded-lg px-3 py-2">
              <Search className="h-4 w-4 text-[#606060]" />
              <input
                value={agentQ}
                onChange={(e) => { setAgentQ(e.target.value); if (form.agent_id) setField('agent_id', null); }}
                placeholder={isAr ? 'ابحث بالاسم / البريد / الهاتف…' : 'Search by name / email / phone…'}
                className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-[#606060]"
                data-testid="agent-search-input"
              />
              {form.agent_id && (
                <button onClick={clearAgent} className="text-red-400 hover:text-red-300">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {agentSearching && <p className="text-xs text-[#606060] mt-1">{isAr ? 'جاري البحث…' : 'Searching…'}</p>}
            {agentResults.length > 0 && !form.agent_id && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-[#0a0a0a] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {agentResults.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => pickAgent(u)}
                    className="w-full text-start px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0"
                    data-testid={`agent-search-result-${u.id}`}
                  >
                    <div className="text-sm text-white">{u.name || u.email}</div>
                    <div className="text-xs text-[#808080]">{u.email} {u.phone && `· ${u.phone}`}</div>
                  </button>
                ))}
              </div>
            )}
            {form.agent_id && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <ReadonlyChip label={isAr ? 'الاسم' : 'Name'}  value={form.agent_name} />
                <ReadonlyChip label={isAr ? 'البريد' : 'Email'} value={form.agent_email} />
                <ReadonlyChip label={isAr ? 'الهاتف' : 'Phone'} value={form.agent_phone} />
              </div>
            )}
          </div>
        )}

        {agentMode === 'new' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>{isAr ? 'اسم الوكيل' : 'Agent name'}</Label>
              <Input value={form.agent_name} onChange={(e) => setField('agent_name', e.target.value)} data-testid="agent-new-name" />
            </div>
            <div>
              <Label>{isAr ? 'البريد الإلكتروني' : 'Email'}</Label>
              <Input type="email" value={form.agent_email} onChange={(e) => setField('agent_email', e.target.value)} data-testid="agent-new-email" />
            </div>
            <div>
              <Label>{isAr ? 'رقم الهاتف' : 'Phone'}</Label>
              <Input value={form.agent_phone} onChange={(e) => setField('agent_phone', e.target.value)} data-testid="agent-new-phone" />
            </div>
          </div>
        )}
      </motion.section>

      {/* 2.5 Linked agent task (iter-200) — appears only when an agent is chosen */}
      {agentMode !== 'none' && (
        <LinkedAgentTaskPicker
          agentEmail={form.agent_email}
          selectedId={form.linked_agent_task_id}
          selectedRef={form.linked_agent_task_ref}
          selectedSnapshot={form.linked_agent_task_snapshot}
          onSelect={handleLinkedTask}
        />
      )}

      {/* 3. Editable fields (unchanged) */}
      <motion.section
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6 space-y-4"
      >
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-5 w-5 text-cyan-400" />
          <h2 className="font-semibold text-white">
            {isAr ? 'بيانات الشركة' : 'Company details'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>{isAr ? 'الاسم التجاري (عربي)' : 'Trade name (Arabic)'}</Label>
            <Input value={form.company_name_ar} onChange={(e) => setField('company_name_ar', e.target.value)} data-testid="agent-fld-name-ar" /></div>
          <div><Label>{isAr ? 'الاسم التجاري (إنجليزي)' : 'Trade name (English)'}</Label>
            <Input value={form.company_name_en} onChange={(e) => setField('company_name_en', e.target.value)} data-testid="agent-fld-name-en" /></div>
          <div><Label>{isAr ? 'رقم الرخصة' : 'License number'}</Label>
            <Input value={form.license_number} onChange={(e) => setField('license_number', e.target.value)} data-testid="agent-fld-license-number" /></div>
          <div><Label>{isAr ? 'الرقم الموحد للرخصة' : 'Unified license no.'}</Label>
            <Input value={form.unified_license_number} onChange={(e) => setField('unified_license_number', e.target.value)} data-testid="agent-fld-unified" /></div>
          <div><Label>{isAr ? 'جهة الإصدار' : 'Issuing authority'}</Label>
            <Input value={form.issuing_authority} onChange={(e) => setField('issuing_authority', e.target.value)} data-testid="agent-fld-authority" /></div>
          <div><Label>{isAr ? 'نوع الرخصة' : 'License type'}</Label>
            <Input value={form.license_type} onChange={(e) => setField('license_type', e.target.value)} data-testid="agent-fld-license-type" /></div>
          <div><Label>{isAr ? 'الشكل القانوني' : 'Legal form'}</Label>
            <Input value={form.legal_form} onChange={(e) => setField('legal_form', e.target.value)} data-testid="agent-fld-legal-form" /></div>
          <div><Label>{isAr ? 'رأس المال' : 'Capital'}</Label>
            <Input value={form.capital} onChange={(e) => setField('capital', e.target.value)} data-testid="agent-fld-capital" /></div>
          <div><Label>{isAr ? 'تاريخ الإصدار' : 'Issue date'}</Label>
            <Input type="date" value={form.issue_date} onChange={(e) => setField('issue_date', e.target.value)} data-testid="agent-fld-issue-date" /></div>
          <div><Label>{isAr ? 'تاريخ الانتهاء' : 'Expiry date'}</Label>
            <Input type="date" value={form.expiry_date} onChange={(e) => setField('expiry_date', e.target.value)} data-testid="agent-fld-expiry-date" /></div>
          <div><Label>{isAr ? 'ص.ب' : 'PO box'}</Label>
            <Input value={form.po_box} onChange={(e) => setField('po_box', e.target.value)} data-testid="agent-fld-pobox" /></div>
          <div><Label>{isAr ? 'الهاتف' : 'Phone'} <span className="text-white/30 text-[10px]">{isAr ? '(اختياري)' : '(optional)'}</span></Label>
            <Input
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              placeholder="+971 50 000 0000"
              className={form.phone && !isValidPhone(form.phone) ? 'border-red-500/60 focus-visible:ring-red-500' : ''}
              data-testid="agent-fld-phone"
            />
            {form.phone && !isValidPhone(form.phone) && (
              <p className="text-[11px] text-red-400 mt-1">{isAr ? 'رقم الهاتف غير صحيح' : 'Invalid phone number'}</p>
            )}
          </div>
          <div className="md:col-span-2"><Label>{isAr ? 'العنوان' : 'Address'}</Label>
            <Input value={form.address} onChange={(e) => setField('address', e.target.value)} data-testid="agent-fld-address" /></div>
          <div className="md:col-span-2"><Label>{isAr ? 'الإيميل' : 'Email'} <span className="text-white/30 text-[10px]">{isAr ? '(اختياري)' : '(optional)'}</span></Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="info@example.com"
              className={form.email && !isValidEmail(form.email) ? 'border-red-500/60 focus-visible:ring-red-500' : ''}
              data-testid="agent-fld-email"
            />
            {form.email && !isValidEmail(form.email) && (
              <p className="text-[11px] text-red-400 mt-1">{isAr ? 'البريد الإلكتروني غير صحيح' : 'Invalid email'}</p>
            )}
          </div>
        </div>

        {/* Activities */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label>{isAr ? 'الأنشطة' : 'Activities'}</Label>
            <Button size="sm" variant="outline" className="border-[#2a2a4e] rounded-lg" onClick={addActivity} data-testid="agent-add-activity-btn">
              <Plus className="h-3.5 w-3.5 me-1" />
              {isAr ? 'إضافة نشاط' : 'Add activity'}
            </Button>
          </div>
          <div className="space-y-2">
            {form.activities.length === 0 && <p className="text-xs text-[#606060]">{isAr ? 'لا توجد أنشطة' : 'No activities yet'}</p>}
            {form.activities.map((a, idx) => (
              <div key={idx} className="flex gap-2" data-testid={`agent-activity-row-${idx}`}>
                <Input value={a} onChange={(e) => setActivity(idx, e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removeActivity(idx)}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Partners */}
        <div className="border-t border-white/10 pt-4">
          <div className="flex items-center justify-between mb-3">
            <Label>{isAr ? 'الشركاء' : 'Partners'}</Label>
            <Button size="sm" variant="outline" className="border-[#2a2a4e] rounded-lg" onClick={addPartner} data-testid="agent-add-partner-btn">
              <Plus className="h-3.5 w-3.5 me-1" />
              {isAr ? 'إضافة شريك' : 'Add partner'}
            </Button>
          </div>
          <div className="space-y-2">
            {form.partners.length === 0 && <p className="text-xs text-[#606060]">{isAr ? 'لا يوجد شركاء' : 'No partners yet'}</p>}
            {form.partners.map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr,180px,44px] gap-2" data-testid={`agent-partner-row-${idx}`}>
                <Input placeholder={isAr ? 'الاسم' : 'Name'} value={p.name || ''} onChange={(e) => setPartner(idx, 'name', e.target.value)} />
                <Input placeholder={isAr ? 'النسبة %' : 'Share %'} value={p.share || ''} onChange={(e) => setPartner(idx, 'share', e.target.value)} />
                <Button variant="ghost" size="icon" onClick={() => removePartner(idx)}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label>{isAr ? 'ملاحظات' : 'Notes'}</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="agent-fld-notes" />
        </div>
      </motion.section>

      <div className="flex justify-end gap-3">
        <Button variant="outline" className="border-[#2a2a4e] rounded-xl text-white" onClick={() => navigate('/manage/cloudgm-agent')}>
          {isAr ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
          data-testid="agent-company-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
          {isAr ? 'حفظ والعودة للقائمة الرئيسية' : 'Save & back to hub'}
        </Button>
      </div>
    </AgentPageShell>
  );
};

const ReadonlyChip = ({ label, value }) => (
  <div className="bg-[#050505] border border-white/10 rounded-lg px-2 py-1.5">
    <div className="text-[10px] text-[#606060]">{label}</div>
    <div className="text-white text-xs truncate">{value || '—'}</div>
  </div>
);

export default AgentCompanyCreatePage;
